import { translate } from '../i18n';
import {
    CapacityPricingWarningsDto,
} from '../models/court/dto/capacity-pricing-warning.dto';
import {
    CommitmentCoverageLossWarningDto,
    CommitmentCoverageLossWarningsDto,
} from '../models/court/dto/commitment-coverage-loss-warning.dto';
import {
    CommitmentRepriceWarningDto,
    CommitmentRepriceWarningsDto,
} from '../models/court/dto/commitment-reprice-warning.dto';
import { buildCapacityPricingLossWarning } from './capacity-pricing-warnings';

/**
 * What an availability write changed for bookings that are ALREADY sold.
 *
 * The third thing a schedule save or an availability delete can do, and the one
 * that used to happen in total silence. The API refuses a write that would
 * STRAND a live booking (it would 404 on every join) and warns when a size
 * stops selling entirely; between those two sits a write that keeps the booking
 * priceable at a DIFFERENT amount. Nobody is stranded, so it is never blocked —
 * but everyone who has not joined yet, and anyone redeeming a replacement link
 * on that game, pays the new price, while the organiser's own settled charge
 * does not move.
 *
 * That is a legitimate thing for an operator to do (a price follows the slot it
 * is sold at), so this is disclosure, not an error: it is worth ten seconds of
 * an operator's attention on the save that caused it, and nothing more.
 */
export function buildCommitmentRepriceWarning(
    result: CommitmentRepriceWarningsDto | undefined | null
): string | null {
    const rows: CommitmentRepriceWarningDto[] = result?.repricedCommitments ?? [];

    const sentences = rows
        // The API returns a row only for a group it actually repriced, so a
        // `count` of 0 is an API that predates the field or a shape nobody
        // should render a toast for. Guarded rather than trusted.
        .filter(row => row.count > 0)
        .map(row =>
            translate('courts.commitments_repriced')
                .replace(/\{\{count\}\}/g, String(row.count))
                .replace(/\{\{sport\}\}/g, row.sport)
                .replace(/\{\{capacity\}\}/g, String(row.capacity))
                .replace(/\{\{fromPrice\}\}/g, String(row.fromPrice))
                .replace(/\{\{toPrice\}\}/g, String(row.toPrice))
                .replace(/\{\{date\}\}/g, formatEarliest(row.earliestStartTime))
        );

    return sentences.length > 0 ? sentences.join(' ') : null;
}

/**
 * What an availability write took away ENTIRELY from bookings that are already
 * sold: their price.
 *
 * The severe sibling of the reprice above, and the last of the three to reach
 * anyone. The API refuses a write that would strand a booking at its CAPACITY
 * while the slot keeps selling at other sizes; it does not refuse — and cannot,
 * without newly rejecting routine schedule edits platform-wide — a write that
 * stops selling the slot at all, which is what narrowing a court's hours or
 * dropping a sport over a booked game does. Those bookings then 404 on every
 * join, every quote and every replacement redemption until the slot is priced
 * again. That reached a `warn` line and nothing else for the whole life of the
 * guard (D165).
 *
 * Not an error and never a reason to treat the write as failed: the save
 * committed, and an operator re-working a schedule in stages is in a legitimate
 * intermediate state. It is the one sentence in this toast that names something
 * already-paying players are blocked by, so it leads.
 */
export function buildCommitmentCoverageLossWarning(
    result: CommitmentCoverageLossWarningsDto | undefined | null
): string | null {
    const rows: CommitmentCoverageLossWarningDto[] =
        result?.unpricedCommitments ?? [];

    const sentences = rows
        // The API returns a row only for a group it actually un-priced, so a
        // `count` of 0 is an API that predates the field or a shape nobody
        // should render a toast for. Guarded rather than trusted, like the
        // sibling above.
        .filter(row => row.count > 0)
        .map(row =>
            translate('courts.commitments_unpriced')
                .replace(/\{\{count\}\}/g, String(row.count))
                .replace(/\{\{sport\}\}/g, row.sport)
                .replace(/\{\{capacity\}\}/g, String(row.capacity))
                .replace(/\{\{date\}\}/g, formatEarliest(row.earliestStartTime))
        );

    return sentences.length > 0 ? sentences.join(' ') : null;
}

/**
 * Everything an availability WRITE can do quietly, as one sentence.
 *
 * Shared by the two rails that can do all three — the compact schedule save and
 * the availability delete — so neither grows its own idea of which warnings
 * belong together or in which order. Ordered by what it costs the operator to
 * ignore: a booking nobody can join at all, then a size that stopped selling,
 * then a price that merely moved.
 *
 * `PUT /courts/:id` deliberately does NOT use this: that response carries the
 * loss field and cannot carry a reprice or a coverage loss, since it rewrites no
 * pricing row.
 */
export function buildAvailabilityWriteWarning(
    result:
        | (CapacityPricingWarningsDto &
              CommitmentRepriceWarningsDto &
              CommitmentCoverageLossWarningsDto)
        | undefined
        | null
): string | null {
    const sentences = [
        buildCommitmentCoverageLossWarning(result),
        buildCapacityPricingLossWarning(result),
        buildCommitmentRepriceWarning(result),
    ].filter((sentence): sentence is string => Boolean(sentence));

    return sentences.length > 0 ? sentences.join(' ') : null;
}

/**
 * The soonest affected booking, as a date an operator can find in their
 * calendar. Falls back to the raw value rather than rendering "Invalid Date" if
 * the API ever sends something unparseable.
 */
function formatEarliest(isoDate: string): string {
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) {
        return isoDate;
    }

    return parsed.toLocaleString();
}
